//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import angular from 'angular';
import { INgRedux } from 'ng-redux';
import { PojoSet } from 'pojo-sets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as RightsAutomationActions from '@account-settings/redux/rights-management/automateRightsRequestingSlice';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import {
  GalleriesClientType,
  getGalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { IToastNotification } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import ReelId from '@models/ReelId';
import { RightsCreationSource } from '@models/RightsCreationSource';
import { UniversalItemType } from '@models/UniversalItemType';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { approveForGalleriesActions as ApproveForGalleriesActions } from '@uia/approve-for-galleries/approve-for-galleries-slice';
import { getIsItemInAllReels } from '@uia/approve-for-galleries/getIsItemInAllReels';
import { getAreNoCheckboxesSelected } from '@uia/approve-for-galleries/getAreNoCheckboxesSelected';
import RightsRequestActions from '@uia/rights-request/rights-request-actions';
import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import { ReduxDispatch } from 'app/curalate.store';
import { getFeatureNames } from 'app/directives/fanreel-product-name/utils';
import { RoutableStateName } from 'app/top-level-states';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';

const mapStateToProps = (state: ICuralateStore) => {
  const approveForGalleriesData = state.uiaModal.approveForGalleries;
  const rightsRequestData = state.uiaModal.rightsRequest;
  const selectedItemData = state.uiaModal.selectedItem;
  const automateRightsRequests =
    state.accountSettings.rightsManagement.automateRightsRequests;

  return {
    selectedItemMetadata: selectedItemData.selectedItemMetadata,
    itemInAllGalleries: getIsItemInAllReels(state),
    gallerySelection: approveForGalleriesData.gallerySelection,
    canRequestGraphRights: rightsRequestData.canGraphRequestRights,
    bypassRightsRequest: rightsRequestData.bypassRightsRequest,
    customRightsRequest: rightsRequestData.customRightsRequest,
    rights_requests_twitter: state.session.features.rights_requests_twitter,
    areNoCheckboxesSelected: getAreNoCheckboxesSelected(state),
    isRightsManagementEnabled: state.session.features.rights_management_page,
    isRightsAutomationEnabled:
      automateRightsRequests.automationConfigurations.isAutomated,
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    rightsActions: bindActionCreators(RightsRequestActions, dispatch),
    approveForGalleriesActions: bindActionCreators(
      ApproveForGalleriesActions,
      dispatch,
    ),
    rightsAutomationActions: bindActionCreators(
      RightsAutomationActions,
      dispatch,
    ),
    galleriesDataActions: bindActionCreators(galleriesDataActions, dispatch),
  };
};

interface IOwnProps {
  onClose: (galleries: PojoSet<ReelId>) => void;
  trackMediaEvent: (eventName: MediaTrackingEventName) => void;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  IOwnProps;

class ApproveForGalleriesButtons extends React.Component<Props> {
  public render() {
    const {
      itemInAllGalleries,
      selectedItemMetadata,
      bypassRightsRequest,
      canRequestGraphRights,
      rights_requests_twitter,
      onClose,
      isRightsAutomationEnabled,
      gallerySelection,
      isRightsManagementEnabled,
    } = this.props;
    let network: string | undefined;
    let requestStatus: RIGHTS_STATUS | undefined;
    let rightsCreationSource: RightsCreationSource | undefined;
    let rightsApprovedViaRelationship: boolean = false;
    let rightsManuallyApprovedOrSkipped = false;

    if (selectedItemMetadata) {
      if (selectedItemMetadata.sourceNetwork) {
        network = selectedItemMetadata.sourceNetwork.toLowerCase();
      } else {
        const { itemType } = Cuid.parse(selectedItemMetadata.rootCuid);
        network = itemType;
      }
      requestStatus = selectedItemMetadata.rightsRequestStatus.status;

      // Rights are approved by a client to client or client to influencer relationship
      rightsCreationSource =
        selectedItemMetadata.rightsPermissions?.creationSource;
      rightsApprovedViaRelationship =
        rightsCreationSource ===
          RightsCreationSource.RelationshipInfluencerToClient ||
        rightsCreationSource ===
          RightsCreationSource.RelationshipClientToClient ||
        rightsCreationSource === RightsCreationSource.SkippedRightsRequest;
      rightsManuallyApprovedOrSkipped =
        rightsCreationSource === RightsCreationSource.ManuallySet &&
        (!selectedItemMetadata.rightsPermissions?.isRightsExpired ||
          !!selectedItemMetadata.rightsPermissions?.isRightsSkipped);
    }

    // Rights Request has been completed
    const rightsComplete = requestStatus === RIGHTS_STATUS.APPROVED;

    // Rights request is either completed or in some intermediary state
    const rightsPendingOrComplete =
      requestStatus !== RIGHTS_STATUS.UNSENT &&
      requestStatus !== RIGHTS_STATUS.EXPIRED;

    // Item is in all galleries and rights request has been completed
    const isNoOp = itemInAllGalleries && rightsComplete;

    // Rights request has expired, offer to re-request
    const reRequestRights = requestStatus === RIGHTS_STATUS.EXPIRED;

    // Can we request rights via graph api?
    const canRequestGraphApiRights =
      (network === UniversalItemType.Instagram ||
        network === UniversalItemType.Facebook) &&
      canRequestGraphRights;

    // Can we request rights via twitter api?
    const canRequestTwitterRights =
      network === UniversalItemType.Twitter && rights_requests_twitter;

    // Can we request rights via api?
    const canRequestApiRights =
      canRequestGraphApiRights || canRequestTwitterRights;

    let returnButton = renderPrimaryButton({
      text: 'Approve for Galleries',
      onClick: () => this.addToGalleriesAndClose(false),
    });

    if (this.isButtonDisabled()) {
      returnButton = renderPrimaryButton({
        text: 'Approve for Galleries',
        isDisabled: true,
      });
    } else if (isNoOp) {
      returnButton = renderPrimaryButton({
        text: 'Done',
        onClick: () => onClose(gallerySelection),
      });
    } else if (bypassRightsRequest) {
      returnButton = renderPrimaryButton({
        text: 'Skip Rights and Approve for Galleries',
        onClick: () => this.addToGalleriesAndClose(true),
      });
    } else if (reRequestRights && canRequestApiRights) {
      returnButton = renderPrimaryButton({
        text: 'Extend Rights Request',
        onClick: () => this.addToGalleriesAndClose(false),
      });
    } else if (
      rightsPendingOrComplete ||
      rightsApprovedViaRelationship ||
      rightsManuallyApprovedOrSkipped ||
      !isRightsManagementEnabled
    ) {
      returnButton = renderPrimaryButton({
        text: 'Approve for Galleries',
        onClick: () => this.addToGalleriesAndClose(false),
      });
    } else if (canRequestApiRights && !isRightsAutomationEnabled) {
      returnButton = renderPrimaryButton({
        text: 'Approve for Galleries',
        onClick: () => this.addToGalleriesAndClose(false),
      });
    } else if (canRequestApiRights && isRightsAutomationEnabled) {
      const onClick =
        network === UniversalItemType.Twitter
          ? this.sendRightsRequestAndApprove
          : this.sendAutomateGraphRequestAndApprove;
      returnButton = renderPrimaryButton({
        text: 'Approve for Galleries',
        onClick,
      });
    }

    return returnButton;
  }

  private addToGalleriesAndClose = async (shouldSkipRights: boolean) => {
    const {
      approveForGalleriesActions,
      onClose,
      trackMediaEvent,
      gallerySelection,
    } = this.props;

    trackMediaEvent(MediaTrackingEventName.AddedToGallery);
    await approveForGalleriesActions.addItemToGalleries(shouldSkipRights);
    onClose(gallerySelection);
  };

  // TODO: PD-272552 Remove sendGraphRequestAndApprove as this is not working as expected because customRightsRequest is never being set in redux
  private sendGraphRequestAndApprove = async () => {
    const {
      approveForGalleriesActions,
      rightsActions,
      selectedItemMetadata,
      customRightsRequest,
      onClose,
      gallerySelection,
    } = this.props;

    await rightsActions.sendGraphRightsRequest(
      selectedItemMetadata.rootCuid,
      customRightsRequest,
    );
    await approveForGalleriesActions.addItemToGalleries();
    onClose(gallerySelection);
  };

  private sendAutomateGraphRequestAndApprove = async () => {
    const {
      approveForGalleriesActions,
      rightsAutomationActions,
      selectedItemMetadata,
      onClose,
      gallerySelection,
    } = this.props;

    const partialCuid = Cuid.toPartial(selectedItemMetadata.rootCuid);

    await rightsAutomationActions.sendMediasToRightAutomation([partialCuid]);
    await approveForGalleriesActions.addItemToGalleries();
    onClose(gallerySelection);
  };

  private sendRightsRequestAndApprove = async () => {
    const {
      approveForGalleriesActions,
      rightsActions,
      selectedItemMetadata,
      customRightsRequest,
      onClose,
      gallerySelection,
    } = this.props;

    await rightsActions.sendRightsRequest(
      selectedItemMetadata.rootCuid,
      customRightsRequest,
    );
    await approveForGalleriesActions.addItemToGalleries();
    onClose(gallerySelection);
  };

  private isButtonDisabled() {
    const { areNoCheckboxesSelected } = this.props;
    return areNoCheckboxesSelected;
  }
}

const renderPrimaryButton = ({
  text,
  onClick = () => undefined,
  isDisabled = false,
}) => {
  return (
    <button
      className='btn cu-button-primary pull-right'
      onClick={onClick}
      disabled={isDisabled}
    >
      {text}
    </button>
  );
};

const ConnectedGalleriesButtons = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ApproveForGalleriesButtons);

const angReactController = makeAngularReactController(
  <ConnectedGalleriesButtons
    onClose={() => undefined}
    trackMediaEvent={(event) => undefined}
  />,
) as any[];
const injected = angReactController.slice(0, -1);

angular.module('uia').component('universalImageActionsGalleriesButtons', {
  controller: [
    '$ngRedux',
    '$scope',
    'UniversalImageActionsService',
    'UniversalImageActionsView',
    ...injected,
    async (
      $ngRedux: INgRedux,
      $scope,
      UniversalImageActionsService,
      UniversalImageActionsView,
      ...dependencies
    ) => {
      const galleriesClientType = getGalleriesClientType($ngRedux.getState());
      const galleriesProductName = getFeatureNames(galleriesClientType).default;

      // If we're an InstaShop client, we want the success toast to link out to /instashop
      const targetState: RoutableStateName =
        galleriesClientType === GalleriesClientType.galleries ||
        galleriesClientType === GalleriesClientType.custom
          ? 'galleries'
          : 'instashop';

      if (!UniversalImageActionsService.itemSupportsRightsRequests()) {
        UniversalImageActionsService.getCurrentWorkflow().removeState(
          UniversalImageActionsView.RightsRequest,
        );
      }

      const onClose = (selectedGalleries: PojoSet<ReelId>) => {
        const numSelectedGalleries = Object.keys(selectedGalleries).length;
        let successToast: IToastNotification;
        if (numSelectedGalleries > 0) {
          successToast = {
            title: `Sent Media to ${galleriesProductName}`,
            message: `Successfully sent media to ${galleriesProductName}`,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              extraData: {
                callback: () => navigateToState(targetState),
                linkText: `Go To ${galleriesProductName}`,
              },
            },
          };
        }
        $scope.$emit('uia:modal:close', {
          result: {
            successToast,
          },
        });
      };

      const activeAngReactController = makeAngularReactController(
        <ConnectedGalleriesButtons
          onClose={onClose}
          trackMediaEvent={UniversalImageActionsService.trackMediaEvent}
        />,
      ) as any[];
      const controllerFunc = activeAngReactController.pop();
      controllerFunc(...dependencies);
    },
  ],
});

export default ConnectedGalleriesButtons;
